import moment from 'moment';
import { appContext } from '../AppContext';
import { DateFormat, ProfileState } from '../profile/profile-types';
import { FilterTimeRange } from 'gf-types';

export type DateTimeFormatInfo = { shortDatePattern: string; shortTimePattern: string; fullDateTimePattern: string };

export const storeContainer = {};

export function getDateTimeFormatInfo() {
  const state = appContext.store.getState();

  const isPublicForm = !!state.formeditor.sharedAccessToken;

  let shortDateFormat: string;
  let shortTimeFormat: string;

  if (isPublicForm) {
    shortDateFormat = state.formeditor.dateFormat?.shortDate;
    shortTimeFormat = state.formeditor.dateFormat?.shortTime;
  } else {
    const dateFormat = getDateFormat(state.profile, 'dateFormat');

    shortDateFormat = dateFormat.shortDateFormat;
    shortTimeFormat = dateFormat.shortTimeFormat;
  }

  const currentLocalData = moment.localeData();
  const shortDatePattern = shortDateFormat || currentLocalData.longDateFormat('L');
  const shortTimePattern = shortTimeFormat || currentLocalData.longDateFormat('LT');
  const fullDateTimePattern = `${shortDatePattern} ${shortTimePattern}`;

  return {
    shortDatePattern,
    shortTimePattern,
    fullDateTimePattern
  };
}

export function getRawDateTimeFormatInfo(): DateTimeFormatInfo {
  const state = appContext.store.getState();

  const dateFormat = getDateFormat(state.profile, 'rawDateFormat');

  const shortDateFormat: string = dateFormat.shortDateFormat;
  const shortTimeFormat: string = dateFormat.shortTimeFormat;
  const dateTimeFormat: string = dateFormat.dateTimeFormat;

  const shortDatePattern = shortDateFormat || 'yyyy-MM-dd';
  const shortTimePattern = shortTimeFormat || 'h:mm tt';
  const fullDateTimePattern = dateTimeFormat || `${shortDatePattern} ${shortTimePattern}`;

  return {
    shortDatePattern,
    shortTimePattern,
    fullDateTimePattern
  };
}

/**
 * Convert .net date format to momentjs format.
 *
 * @param format The .net date format string.
 */
export function convertFieldFormatToMoment(format: string) {
  let momentFormat = format;
  if (momentFormat) {
    momentFormat = momentFormat.replace(/yyyy/g, 'YYYY');
    momentFormat = momentFormat.replace(/yy/g, 'YY');
    momentFormat = momentFormat.replace(/dd/g, 'DD');
    momentFormat = momentFormat.replace(/d/g, 'D');
    momentFormat = momentFormat.replace(/tt/g, 'A');
  }
  return momentFormat;
}

// Updating invalid date format set by create account
export function fixDotNetDateFormat(format: string) {
  if (format == null) {
    return format;
  }

  let retval = format;
  retval = retval.replace(/S/g, '');
  retval = retval.replace(/Y/g, 'yyyy');
  retval = retval.replace(/i/g, 'mm');
  retval = retval.replace(/F/g, 'MMMM');
  retval = retval.replace(/A/g, 'tt');
  return retval;
}

export const getDateTimeFormat = () => {
  return getDateTimeFormatInfo().fullDateTimePattern;
};

export const formatAsShortDate = (date) => {
  const format = getDateTimeFormatInfo().shortDatePattern;
  return moment(date).format(format);
};

export const formatUtcDateAsLocalDateTime = (date) => {
  return moment.utc(date).local().format(getDateTimeFormat());
};

export const formatUtcDateAsLocalDate = (date) => {
  const format = getDateTimeFormatInfo().shortDatePattern;
  return moment.utc(date).local().format(format);
};

export const formatUtcDateAsLocalTime = (date) => {
  const format = getDateTimeFormatInfo().shortTimePattern;
  return moment.utc(date).local().format(format);
};

export const isUtcDatePassed = (date) => {
  if (!date) {
    return false;
  }
  const current = new Date();
  return moment.utc(date).local().toDate() < current;
};

export const is24Hrs = (formatInfo) => {
  return formatInfo.indexOf('H') > -1;
};

function getDateFormat(profile: ProfileState, prop: 'dateFormat' | 'rawDateFormat') {
  const dateFormat = profile[prop] as DateFormat;

  return {
    shortDateFormat: dateFormat?.shortDate,
    shortTimeFormat: dateFormat?.shortTime,
    dateTimeFormat: dateFormat?.dateTime
  };
}

export const getTimeRange = (timeRange: FilterTimeRange, clientCreatedDate?: string) => {
  let newStartDate = new Date();
  let newEndDate = new Date();
  switch (timeRange) {
    case 'today':
      newStartDate.setHours(0);
      newStartDate.setMinutes(0);
      newStartDate.setSeconds(0);

      newEndDate.setHours(23);
      newEndDate.setMinutes(59);
      newEndDate.setSeconds(59);
      break;

    case 'yesterday':
      newStartDate.setDate(newStartDate.getDate() - 1);
      newStartDate.setHours(0);
      newStartDate.setMinutes(0);
      newStartDate.setSeconds(0);

      newEndDate.setDate(newEndDate.getDate() - 1);
      newEndDate.setHours(23);
      newEndDate.setMinutes(59);
      newEndDate.setSeconds(59);
      break;

    case 'thisweek':
      newStartDate.setDate(newStartDate.getDate() - newStartDate.getDay());
      newStartDate.setHours(0);
      newStartDate.setMinutes(0);
      newStartDate.setSeconds(0);

      newEndDate = new Date(newStartDate);
      newEndDate.setDate(newStartDate.getDate() + 6);
      newEndDate.setHours(23);
      newEndDate.setMinutes(59);
      newEndDate.setSeconds(59);
      break;

    case 'lastweek':
      newStartDate.setDate(newStartDate.getDate() - 7);
      newStartDate.setDate(newStartDate.getDate() - newStartDate.getDay());
      newStartDate.setHours(0);
      newStartDate.setMinutes(0);
      newStartDate.setSeconds(0);

      newEndDate = new Date(newStartDate);
      newEndDate.setDate(newStartDate.getDate() + 6);
      newEndDate.setHours(23);
      newEndDate.setMinutes(59);
      newEndDate.setSeconds(59);
      break;

    case 'thismonth':
      newStartDate.setDate(1);
      newEndDate.setMonth(newStartDate.getMonth() + 1);
      newEndDate.setDate(0);
      newEndDate.setHours(23);
      newEndDate.setMinutes(59);
      newEndDate.setSeconds(59);
      break;

    case 'lastmonth':
      newStartDate.setDate(1);
      newStartDate.setMonth(newStartDate.getMonth() - 1);
      newStartDate.setHours(0);
      newStartDate.setMinutes(0);
      newStartDate.setSeconds(0);

      newEndDate.setDate(0);
      newEndDate.setHours(23);
      newEndDate.setMinutes(59);
      newEndDate.setSeconds(59);
      break;

    case 'yeartodate':
      newStartDate = new Date(newStartDate.getFullYear(), 0, 1);
      newStartDate.setHours(0);
      newStartDate.setMinutes(0);
      newStartDate.setSeconds(0);

      newEndDate.setHours(23);
      newEndDate.setMinutes(59);
      newEndDate.setSeconds(59);
      break;

    case 'lastyear':
      newStartDate = new Date(newStartDate.getFullYear() - 1, 0, 1);
      newStartDate.setHours(0);
      newStartDate.setMinutes(0);
      newStartDate.setSeconds(0);

      newEndDate = new Date(newStartDate.getFullYear(), 11, 31);
      newEndDate.setHours(23);
      newEndDate.setMinutes(59);
      newEndDate.setSeconds(59);
      break;

    case 'alltime':
      newStartDate = clientCreatedDate ? new Date(clientCreatedDate) : null;
      newEndDate.setHours(23);
      newEndDate.setMinutes(59);
      newEndDate.setSeconds(59);
      break;

    default:
      newStartDate.setDate(newStartDate.getDate() - (Number(timeRange) || 90));
      newStartDate.setHours(0);
      newStartDate.setMinutes(0);
      newStartDate.setSeconds(0);

      newEndDate.setHours(23);
      newEndDate.setMinutes(59);
      newEndDate.setSeconds(59);
  }

  return {
    startDate: newStartDate,
    endDate: newEndDate
  };
};
